import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePopup } from '../../../contexts/PopupContext';
import { supabase } from '../../../lib/supabase';
import SingleEvent from '../../../classes/events/SingleEvent';
import { getAvatarUrl } from '../../../utils/avatarUtils';
import { getAccountAvatarPlaceholderVariant } from '../../../utils/accountAvatarStatus';
import { AccountAvatarPlaceholder } from '../../common/AccountAvatarPlaceholder';
import { ProfileAvatarPlaceholderIcon } from '../../profile/ProfileDashIcons';
import { getFriendStatus } from '../../../utils/friendsService';
import { getProfileCardDisplayName } from '../../../utils/profileDisplayName';
import './JoinedUsersList.css';

/**
 * Komponent listy użytkowników którzy już dołączyli do eventu
 */
function HiddenParticipantAvatar() {
  return (
    <svg className="joined-users-list-hidden-avatar-svg" viewBox="0 0 48 48" fill="none" aria-hidden="true" focusable="false">
      <circle className="joined-users-list-hidden-avatar-orbit" cx="24" cy="24" r="18" />
      <path className="joined-users-list-hidden-avatar-hood" d="M13 38c1.9-7.1 6.2-10.6 11-10.6S33.1 30.9 35 38" />
      <path className="joined-users-list-hidden-avatar-head" d="M16.8 20.5c0-5 3.1-8.5 7.2-8.5s7.2 3.5 7.2 8.5c0 4.2-3 7.4-7.2 7.4s-7.2-3.2-7.2-7.4Z" />
      <path className="joined-users-list-hidden-avatar-slash" d="M13 35 35 13" />
    </svg>
  );
}

function JoinedUsersList({
  eventId,
  participants,
  participantsCount,
  maxParticipants,
  hostId,
  isHost,
  isParticipant,
  userApplication,
  currentUser,
  eventIsOpen = true,
  onKick,
  onJoin,
  onStatusChange
}) {
  const { t } = useTranslation(['events', 'shared']);
  const { openPopup } = usePopup();
  const [hoveredSlotIndex, setHoveredSlotIndex] = useState(null);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [friendStatusMap, setFriendStatusMap] = useState({});

  // Pobierz profil użytkownika jeśli ma pending zgłoszenie
  useEffect(() => {
    const loadCurrentUserProfile = async () => {
      if (currentUser?.id && userApplication?.status === 'pending') {
        try {
          const { data, error } = await supabase
            .from('users')
            .select('id, name, nickname, first_name, last_name, avatar_url, is_active')
            .eq('id', currentUser.id)
            .single();
          
          if (!error && data) {
            setCurrentUserProfile(data);
          }
        } catch (err) {
          console.error('Error loading current user profile:', err);
        }
      } else {
        setCurrentUserProfile(null);
      }
    };

    loadCurrentUserProfile();
  }, [currentUser?.id, userApplication?.status]);

  // Pobierz status znajomości dla każdego uczestnika
  useEffect(() => {
    const loadFriendStatuses = async () => {
      if (!currentUser?.id || isParticipant || isHost) {
        // Jeśli użytkownik jest uczestnikiem lub hostem, nie potrzebujemy sprawdzać statusu znajomości
        // (wszystko jest widoczne)
        return;
      }

      const statusMap = {};
      
      // Sprawdź status znajomości dla każdego uczestnika równolegle
      const friendStatusPromises = participants.map(async (participant) => {
        if (!participant.user?.id) return null;
        
        try {
          const status = await getFriendStatus(currentUser.id, participant.user.id);
          return { userId: participant.user.id, status };
        } catch (err) {
          console.error(`Error checking friend status for user ${participant.user.id}:`, err);
          return { userId: participant.user.id, status: 'none' };
        }
      });

      const results = await Promise.all(friendStatusPromises);
      results.forEach((result) => {
        if (result) {
          statusMap[result.userId] = result.status;
        }
      });

      setFriendStatusMap(statusMap);
    };

    loadFriendStatuses();
  }, [currentUser?.id, participants, isParticipant, isHost]);

  const handleProfileClick = (userId) => {
    openPopup('profile', { userId }, 0);
  };

  const unknownUser = t('events.details.unknownUser');

  const getUserDisplayName = (user) => {
    if (!user) return unknownUser;
    const allowRealName =
      isParticipant ||
      isHost ||
      (currentUser?.id && user.id === currentUser.id) ||
      friendStatusMap[user.id] === 'accepted';
    return getProfileCardDisplayName(user, {
      allowRealName,
      nameNotSetLabel: unknownUser,
    });
  };

  // Generuj losowy kolor na podstawie ID (deterministyczny - ten sam ID = ten sam kolor)
  const getRandomColorForUser = (userId) => {
    if (!userId) return '#FD7651'; // fallback
    
    // Użyj prostego hash do generowania spójnego koloru
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Generuj ładne, żywe kolory (unikaj zbyt ciemnych lub jasnych)
    const hue = Math.abs(hash) % 360;
    const saturation = 60 + (Math.abs(hash) % 25); // 60-85%
    const lightness = 45 + (Math.abs(hash) % 20); // 45-65%
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Oblicz wolne miejsca
  const filledSlots = participants.length;
  const hasPendingApplication = userApplication?.status === 'pending' && currentUserProfile;
  // Jeśli użytkownik ma pending zgłoszenie, zajmuje jedno miejsce
  const effectiveFilledSlots = hasPendingApplication ? filledSlots + 1 : filledSlots;
  const emptySlots = maxParticipants - effectiveFilledSlots;
  
  // Sprawdź czy użytkownik może dołączyć (nie jest uczestnikiem, nie ma pending zgłoszenia, są wolne miejsca)
  const canJoin = !isParticipant && 
                  userApplication?.status !== 'pending' && 
                  emptySlots > 0;

  return (
    <div className="joined-users-list">
      <div className="joined-users-list-header">
        <h3 className="joined-users-list-title">
          {t('events.details.participants')}
        </h3>
        <span className="joined-users-list-count">
          {effectiveFilledSlots}/{maxParticipants}
        </span>
      </div>

      <div className="joined-users-list-slots">
        {/* Wypełnione miejsca - uczestnicy */}
        {participants.map((participant) => {
          const user = participant.user;
          if (!user) {
            return (
              <div
                key={participant.id}
                className="joined-users-list-slot filled joined-users-list-slot--unknown-user"
              >
                <div className="joined-users-list-slot-avatar joined-users-list-slot-avatar--account-status">
                  <AccountAvatarPlaceholder
                    variant="inactive"
                    title={t('events.details.avatarInactiveAria')}
                  />
                </div>
                <div className="joined-users-list-slot-info">
                  <div className="joined-users-list-slot-name">
                    <span className="joined-users-list-slot-name-text">{unknownUser}</span>
                  </div>
                </div>
              </div>
            );
          }

          const isHostUser = participant.user_id === hostId;
          const isCurrentUser = currentUser?.id === participant.user_id;
          const confirmationStatus = participant.confirmation_status || 'pending';
          const isConfirmed = confirmationStatus === 'confirmed';
          const accountPh = getAccountAvatarPlaceholderVariant(user);
          const accountPhTitle =
            accountPh === 'banned'
              ? t('events.details.avatarBannedAria')
              : t('events.details.avatarInactiveAria');

          // Sprawdź czy powinien być rozmyty
          // Rozmyj tylko jeśli:
          // - użytkownik NIE jest uczestnikiem (isParticipant === false)
          // - użytkownik NIE jest hostem (isHost === false)
          // - uczestnik NIE jest hostem eventu (isHostUser === false)
          // - uczestnik NIE jest aktualnym użytkownikiem
          // - uczestnik NIE jest znajomym (friendStatus !== 'accepted')
          const isFriend = friendStatusMap[user.id] === 'accepted';
          const shouldBlur = !isParticipant && 
                            !isHost && 
                            !isHostUser && 
                            !isCurrentUser && 
                            !isFriend;

          const handleConfirmationToggle = async () => {
            if (!isCurrentUser) return;
            if (!eventIsOpen) {
              alert(t('events.participants.eventClosed'));
              return;
            }

            try {
              const newStatus = isConfirmed ? 'pending' : 'confirmed';
              const { error } = await supabase
                .from('event_participants')
                .update({ 
                  confirmation_status: newStatus,
                  updated_at: new Date().toISOString()
                })
                .eq('id', participant.id);

              if (error) throw error;

              // Pobierz dane użytkownika do wiadomości systemowej
              const userName = getProfileCardDisplayName(user, {
                allowRealName: true,
                nameNotSetLabel: unknownUser,
              });

              // Utwórz wiadomość systemową o zmianie statusu potwierdzenia
              const messageKey = newStatus === 'confirmed' 
                ? 'events.chat.system.confirmationConfirmed'
                : 'events.chat.system.confirmationPending';
              
              await SingleEvent.createSystemMessage(
                eventId,
                t(messageKey, { name: userName })
              );

              // Powiadom parent component o zmianie
              if (onStatusChange) {
                onStatusChange();
              }
            } catch (err) {
              console.error('Error updating confirmation status:', err);
              alert(t('events.participants.errorUpdateConfirmation'));
            }
          };

          const avatarColor = shouldBlur ? getRandomColorForUser(user.id) : null;

          return (
            <div key={participant.id} className={`joined-users-list-slot filled ${shouldBlur ? 'blurred' : ''}`}>
              <div
                className={`joined-users-list-slot-avatar ${shouldBlur ? 'blurred' : ''} ${accountPh ? 'joined-users-list-slot-avatar--account-status' : ''}`.trim()}
                style={shouldBlur && !accountPh ? { '--hidden-avatar-accent': avatarColor } : {}}
              >
                {accountPh ? (
                  <AccountAvatarPlaceholder variant={accountPh} title={accountPhTitle} />
                ) : shouldBlur ? (
                  <HiddenParticipantAvatar />
                ) : user.avatar_url ? (
                  <img
                    src={getAvatarUrl(user.avatar_url, 100)}
                    alt={getUserDisplayName(user)}
                    className="joined-users-list-slot-avatar-img"
                  />
                ) : (
                  <ProfileAvatarPlaceholderIcon className="joined-users-list-default-avatar-svg" size={30} />
                )}
              </div>
              <div className="joined-users-list-slot-info">
                <div className={`joined-users-list-slot-name ${shouldBlur ? 'blurred' : ''}`}>
                  {shouldBlur ? (
                    // Gdy rozmyte, nie wyświetlaj linku z prawdziwym user ID
                    <span className="joined-users-list-slot-name-link blurred joined-users-list-slot-hidden-title">
                      {t('events.participants.hiddenProfile', 'Profil ukryty')}
                    </span>
                  ) : (
                    <>
                      <button
                        className="joined-users-list-slot-name-link"
                        onClick={() => handleProfileClick(user.id)}
                        type="button"
                      >
                        {getUserDisplayName(user)}
                      </button>
                      {isHostUser && (
                        <span className="joined-users-list-slot-host">👑</span>
                      )}
                    </>
                  )}
                </div>
                {!shouldBlur && (
                  <div className={`joined-users-list-slot-status ${isConfirmed ? 'confirmed' : 'pending'}`}>
                    {isConfirmed ? (
                      <>✓ {t('events.participants.confirmed')}</>
                    ) : (
                      <>⏳ {t('events.participants.awaitingConfirmation')}</>
                    )}
                    {isCurrentUser && eventIsOpen && (
                      <button
                        className="joined-users-list-slot-confirmation-toggle"
                        onClick={handleConfirmationToggle}
                        type="button"
                        title={isConfirmed ? t('events.participants.markAsPending') : t('events.participants.markAsConfirmed')}
                      >
                        {isConfirmed ? '↩' : '✓'}
                      </button>
                    )}
                  </div>
                )}
                {shouldBlur && (
                  <div className="joined-users-list-slot-status blurred joined-users-list-slot-hidden-hint">
                    {t('events.participants.hiddenProfileHint', 'Dołącz do wydarzenia, aby zobaczyć członków.')}
                  </div>
                )}
              </div>
              {isHost && eventIsOpen && !isHostUser && !shouldBlur && (
                <button
                  className="joined-users-list-slot-kick"
                  onClick={() => onKick(participant)}
                  type="button"
                  title={t('events.participants.kick')}
                >
                  ✕
                </button>
              )}
            </div>
          );
        })}

        {/* Użytkownik z pending zgłoszeniem */}
        {hasPendingApplication && (
            <div className="joined-users-list-slot pending">
            <div className="joined-users-list-slot-avatar pending">
              {currentUserProfile.avatar_url ? (
                <img 
                  src={getAvatarUrl(currentUserProfile.avatar_url, 100)} 
                  alt={getUserDisplayName(currentUserProfile)}
                  className="joined-users-list-slot-avatar-img"
                />
              ) : (
                <ProfileAvatarPlaceholderIcon className="joined-users-list-default-avatar-svg" size={30} />
              )}
            </div>
            <div className="joined-users-list-slot-info">
              <div className="joined-users-list-slot-name">
                <button
                  className="joined-users-list-slot-name-link"
                  onClick={() => handleProfileClick(currentUserProfile.id)}
                  type="button"
                >
                  {getUserDisplayName(currentUserProfile)}
                </button>
              </div>
              <div className="joined-users-list-slot-status pending">
                ⏳ {t('events.participants.pendingApproval')}
              </div>
            </div>
          </div>
        )}

        {/* Puste miejsca */}
        {Array.from({ length: emptySlots }).map((_, index) => {
          const isHovered = hoveredSlotIndex === index;
          const showJoinText = canJoin && isHovered;
          
          const handleEmptySlotClick = () => {
            if (canJoin && onJoin) {
              if (window.confirm(t('events.join.confirm'))) {
                onJoin();
              }
            }
          };
          
          return (
            <div 
              key={`empty-${index}`} 
              className={`joined-users-list-slot empty ${canJoin ? 'joinable' : ''} ${isHovered ? 'hovered' : ''}`}
              onMouseEnter={() => canJoin && setHoveredSlotIndex(index)}
              onMouseLeave={() => setHoveredSlotIndex(null)}
              onClick={handleEmptySlotClick}
              style={{ cursor: canJoin ? 'pointer' : 'default' }}
            >
              <div className="joined-users-list-slot-avatar empty">
                ?
              </div>
              <div className="joined-users-list-slot-info">
                <div className="joined-users-list-slot-name empty">
                  {showJoinText 
                    ? t('events.participants.occupySpot') 
                    : t('events.participants.freeSpot')}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default JoinedUsersList;
